// @ts-nocheck
import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
    FileText, Plus, Edit, Trash2, Save,
    Sparkles, Loader2, MessageCircle, Megaphone,
    Check, Copy, ChevronDown, ChevronUp
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { api } from '@/api/api-client';
import { listLibraryScripts, saveLibraryScript, deleteLibraryScript } from '@/components/dialer/callScriptLibrary';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import { motion, AnimatePresence } from 'framer-motion';
import { useDialerData } from '@/contexts/DialerDataContext';
import { hasMeetingBooked } from '@/utils/callUtils';
import { useTranslation } from 'react-i18next';

// ─── Script type definitions ───────────────────────────────────────────────────
// `label`/`description` are resolved from the catalog at render time (see
// `useScriptTypes` below); `systemPrompt` stays untranslated, it is an
// instruction sent to the LLM, not interface copy.

const SCRIPT_TYPE_META = [
    {
        id: 'conversational',
        icon: MessageCircle,
        color: 'text-[var(--vf-accent-text)]',
        bgColor: 'bg-[var(--vf-accent-bg)]',
        borderColor: 'border-[var(--vf-accent-bd)]',
        activeBg: 'bg-[var(--vf-accent-bg)]',
        activeBorder: 'border-[var(--vf-accent)]',
        activeText: 'text-[var(--vf-accent-text)]',
        badgeBg: 'bg-[var(--vf-accent-bg)] text-[var(--vf-accent-text)]',
        systemPrompt: (context) => `Tu es un expert en vente consultative et prospection téléphonique.
Génère un script d'appel de style CONVERSATIONNEL pour une campagne avec ces données:

${context}

Ce script doit:
- Commencer par une accroche naturelle et non agressive
- Utiliser des questions ouvertes pour comprendre le contexte du prospect
- Favoriser l'écoute active et les silences
- Éviter tout jargon commercial ou pression
- Construire la confiance avant de mentionner le produit/service
- Inclure des exemples de reformulation
- Terminer par une proposition de valeur douce

Format: sections clairement identifiées (Accroche, Découverte, Reformulation, Proposition, Closing)
Inclure des alternatives pour les principales objections identifiées dans les données.`,
    },
    {
        id: 'pitch',
        icon: Megaphone,
        color: 'text-[var(--vf-accent-text)]',
        bgColor: 'bg-[var(--vf-accent-bg)]',
        borderColor: 'border-[var(--vf-accent-bd)]',
        activeBg: 'bg-[var(--vf-accent-bg)]',
        activeBorder: 'border-[var(--vf-accent)]',
        activeText: 'text-[var(--vf-accent-text)]',
        badgeBg: 'bg-[var(--vf-accent-bg)] text-[var(--vf-accent-text)]',
        systemPrompt: (context) => `Tu es un expert en vente directe et closing téléphonique.
Génère un script d'appel de style PITCH pour une campagne avec ces données:

${context}

Ce script doit:
- Commencer par un accroche percutante qui capte l'attention en moins de 10 secondes
- Présenter une proposition de valeur claire et différenciante
- Utiliser des preuves sociales ou chiffres si pertinents
- Créer un sentiment d'urgence ou de rareté légitime
- Qualifier rapidement avec 2-3 questions clés
- Répondre aux objections avec des contre-arguments forts
- Fermer sur une action concrète (rendez-vous, démo, envoi d'info)

Format: sections clairement identifiées (Accroche, Pitch, Qualification, Objections, Closing)
Adapter le vocabulaire et le ton aux entreprises et secteurs identifiés dans les données.`,
    },
];

// ─── Build call context summary for AI ────────────────────────────────────────

function buildCallContext(allCalls) {
    if (!allCalls || allCalls.length === 0) {
        return 'Aucun historique d\'appels disponible.';
    }

    const total      = allCalls.length;
    const connected  = allCalls.filter(c => ['contacted', 'connected', 'meeting_booked', 'connected_to_agent', 'not_interested', 'callback_requested'].includes(c.call_outcome)).length;
    const meetings   = allCalls.filter(hasMeetingBooked).length;
    const noAnswer   = allCalls.filter(c => c.call_outcome === 'no_answer').length;
    const notInterested = allCalls.filter(c => c.call_outcome === 'not_interested').length;
    const callbacks  = allCalls.filter(c => c.call_outcome === 'callback_requested').length;
    const connectRate = total > 0 ? ((connected / total) * 100).toFixed(1) : '0';

    const companies = [...new Set(allCalls.map(c => c.prospect_company).filter(Boolean))];
    const companySample = companies.slice(0, 15).join(', ');

    const recentNotes = allCalls
        .filter(c => c.notes)
        .slice(-8)
        .map(c => `- ${c.prospect_name || 'Prospect'} (${c.prospect_company || 'N/A'}): ${c.notes}`)
        .join('\n');

    const lines = [
        `Total appels: ${total}`,
        `Taux de contact: ${connectRate}% (${connected}/${total})`,
        `Rendez-vous obtenus: ${meetings}`,
        `Pas de réponse: ${noAnswer}`,
        `Pas intéressés: ${notInterested}`,
        `Rappels demandés: ${callbacks}`,
        companySample ? `Entreprises contactées (échantillon): ${companySample}` : null,
        recentNotes ? `Notes et retours terrain récents:\n${recentNotes}` : null,
    ].filter(Boolean);

    return lines.join('\n');
}

// ─── SuggestionPanel sub-component ────────────────────────────────────────────
// Isolated component so useState hooks are not inside a render function.

function SuggestionPanel({ scriptType, allCalls, clientEmail, queryClient, onSaved, t }) {
    const [generated, setGenerated] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [copied, setCopied] = useState(false);
    const [showSaveDialog, setShowSaveDialog] = useState(false);
    const [saveName, setSaveName] = useState('');
    const [saveDescription, setSaveDescription] = useState('');
    const [isExpanded, setIsExpanded] = useState(false);

    const Icon = scriptType.icon;

    const handleGenerate = async () => {
        setIsGenerating(true);
        setGenerated('');
        setIsExpanded(true);

        try {
            const context = buildCallContext(allCalls);
            const result = await api.integrations.Core.InvokeLLM({
                feature: 'call_scripts',
                prompt: scriptType.systemPrompt(context),
                add_context_from_internet: false,
            });
            const text = typeof result === 'string' ? result : result?.result || result?.text || result?.content || JSON.stringify(result, null, 2);
            setGenerated(text);
            toast.success(t('dialer:callScripts.toast.generated'));
        } catch (err) {
            toast.error(t('dialer:callScripts.toast.generateError'));
            console.error(err);
        } finally {
            setIsGenerating(false);
        }
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(generated);
        setCopied(true);
        toast.success(t('dialer:callScripts.toast.copied'));
        setTimeout(() => setCopied(false), 2000);
    };

    const saveMutation = useMutation({
        mutationFn: async () => {
            return saveLibraryScript({ name: saveName, content: generated, aiGenerated: true });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['call-scripts'] });
            setShowSaveDialog(false);
            setSaveName('');
            setSaveDescription('');
            toast.success(t('dialer:callScripts.toast.savedToLibrary'));
            if (onSaved) onSaved();
        },
        onError: () => {
            toast.error(t('dialer:callScripts.toast.saveError'));
        },
    });

    const handleSave = () => {
        if (!saveName.trim()) {
            toast.error(t('dialer:callScripts.toast.nameRequired'));
            return;
        }
        saveMutation.mutate();
    };

    return (
        <div className={`border-2 rounded-xl overflow-hidden transition-all ${
            generated ? scriptType.activeBorder : scriptType.borderColor
        }`}>
            {/* Header row */}
            <div className={`flex items-center gap-3 p-4 ${generated ? scriptType.activeBg : 'bg-card'}`}>
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${scriptType.bgColor}`}>
                    <Icon className={`w-5 h-5 ${scriptType.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <p className="font-semibold text-sm text-foreground">{scriptType.label}</p>
                        {generated && (
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${scriptType.badgeBg}`}>
                                {t('dialer:callScripts.generatedBadge')}
                            </span>
                        )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{scriptType.description}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    {generated && (
                        <button
                            onClick={() => setIsExpanded(v => !v)}
                            className="p-1.5 rounded-lg hover:bg-black/5 text-muted-foreground transition-colors"
                            title={isExpanded ? t('dialer:callHistory.summary.collapse') : t('dialer:callScripts.viewScript')}
                        >
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                    )}
                    <Button
                        size="sm"
                        onClick={handleGenerate}
                        disabled={isGenerating}
                        className={`h-8 text-xs ${
                            generated
                                ? 'bg-muted text-muted-foreground hover:bg-accent'
                                : 'bg-[var(--vf-primary)] hover:bg-[var(--vf-primary-hover)] text-white'
                        }`}
                    >
                        {isGenerating ? (
                            <>
                                <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                                {t('dialer:callScripts.generating')}
                            </>
                        ) : generated ? (
                            <>
                                <Sparkles className="w-3 h-3 mr-1.5" />
                                {t('dialer:callScripts.regenerate')}
                            </>
                        ) : (
                            <>
                                <Sparkles className="w-3 h-3 mr-1.5" />
                                {t('dialer:callScripts.generate')}
                            </>
                        )}
                    </Button>
                </div>
            </div>

            {/* Generated content */}
            <AnimatePresence>
                {generated && isExpanded && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                    >
                        <div className="border-t border-border">
                            {/* Action bar */}
                            <div className="flex items-center justify-end gap-2 px-4 py-2 bg-muted/30">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleCopy}
                                    className="h-7 text-xs text-muted-foreground"
                                >
                                    {copied ? (
                                        <><Check className="w-3 h-3 mr-1 text-green-600" />{t('common:actions.copied')}</>
                                    ) : (
                                        <><Copy className="w-3 h-3 mr-1" />{t('common:actions.copy')}</>
                                    )}
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                        setSaveName(`${scriptType.label}`);
                                        setShowSaveDialog(true);
                                    }}
                                    className="h-7 text-xs text-green-600 hover:text-green-700 hover:bg-green-50"
                                >
                                    <Save className="w-3 h-3 mr-1" />
                                    {t('dialer:callScripts.save')}
                                </Button>
                            </div>

                            {/* Script content */}
                            <div className="px-4 pb-4 max-h-[400px] overflow-y-auto">
                                <ReactMarkdown
                                    className="prose prose-sm prose-slate max-w-none
                                        prose-headings:text-foreground prose-headings:font-semibold prose-headings:text-sm
                                        prose-p:text-foreground/80 prose-p:leading-relaxed prose-p:text-sm
                                        prose-strong:text-foreground prose-strong:font-semibold
                                        prose-ul:text-foreground/80 prose-ol:text-foreground/80
                                        prose-li:text-sm prose-li:marker:text-[var(--vf-accent-text)]"
                                >
                                    {generated}
                                </ReactMarkdown>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Save Dialog */}
            <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{t('dialer:callScripts.saveDialogTitle')}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <label className="text-sm font-medium mb-2 block">{t('dialer:callScripts.scriptNameLabel')}</label>
                            <Input
                                value={saveName}
                                onChange={(e) => setSaveName(e.target.value)}
                                placeholder={t('dialer:callScripts.scriptNamePlaceholder')}
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium mb-2 block">{t('dialer:callScripts.descriptionOptionalLabel')}</label>
                            <Input
                                value={saveDescription}
                                onChange={(e) => setSaveDescription(e.target.value)}
                                placeholder={t('dialer:callScripts.descriptionPlaceholder')}
                            />
                        </div>
                        <div className="flex gap-2">
                            <Button onClick={() => setShowSaveDialog(false)} variant="outline" className="flex-1">
                                {t('common:actions.cancel')}
                            </Button>
                            <Button
                                onClick={handleSave}
                                disabled={saveMutation.isPending}
                                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                            >
                                {saveMutation.isPending ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                    <Save className="w-4 h-4 mr-2" />
                                )}
                                {t('dialer:callScripts.save')}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// ─── Main component ────────────────────────────────────────────────────────────

export default function CallScripts({ clientEmail }) {
    const { t } = useTranslation(['dialer', 'common']);
    const { allCalls } = useDialerData();
    const [editingScript, setEditingScript] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const queryClient = useQueryClient();

    const scriptTypes = useMemo(() => SCRIPT_TYPE_META.map((meta) => ({
        ...meta,
        label: t(`dialer:callScripts.types.${meta.id}.label`),
        description: t(`dialer:callScripts.types.${meta.id}.description`),
    })), [t]);

    const { data: scripts = [] } = useQuery({
        queryKey: ['call-scripts', clientEmail],
        queryFn: async () => {
            return listLibraryScripts();
        },
        enabled: !!clientEmail,
    });

    const saveMutation = useMutation({
        mutationFn: async (scriptData) => {
            return saveLibraryScript({ id: editingScript?.id, name: scriptData.name, content: scriptData.content });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['call-scripts'] });
            setIsDialogOpen(false);
            setEditingScript(null);
            toast.success(t('dialer:scriptPanel.toast.saved'));
        }
    });

    const deleteMutation = useMutation({
        mutationFn: (scriptId) => deleteLibraryScript(scriptId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['call-scripts'] });
            toast.success(t('dialer:callScripts.toast.deleted'));
        }
    });

    const handleSave = () => {
        if (!editingScript?.name || !editingScript?.content) {
            toast.error(t('dialer:callScripts.toast.nameAndContentRequired'));
            return;
        }
        saveMutation.mutate(editingScript);
    };

    return (
        <div className="space-y-6">
            {/* ── AI Script Suggestions ────────────────────────────────────── */}
            <Card className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm">
                <CardHeader className="bg-[var(--vf-accent-bg)] border-b border-border pb-4">
                    <CardTitle className="flex items-center gap-2 text-foreground">
                        <Sparkles className="w-5 h-5 text-[var(--vf-accent-text)]" />
                        {t('dialer:callScripts.aiSuggestions.title')}
                        <span className="text-xs font-normal text-muted-foreground ml-1">
                            {t('dialer:callScripts.aiSuggestions.subtitle')}
                        </span>
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-3">
                    {scriptTypes.map(scriptType => (
                        <SuggestionPanel
                            key={scriptType.id}
                            scriptType={scriptType}
                            allCalls={allCalls}
                            clientEmail={clientEmail}
                            queryClient={queryClient}
                            onSaved={() => {}}
                            t={t}
                        />
                    ))}
                </CardContent>
            </Card>

            {/* ── Script Library ───────────────────────────────────────────── */}
            <Card className="bg-card/40 backdrop-blur-xl border-white/60">
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <CardTitle className="flex items-center gap-2">
                            <FileText className="w-5 h-5 text-[var(--vf-accent-text)]" />
                            {t('dialer:callScripts.library.title')}
                        </CardTitle>
                        <Button
                            onClick={() => {
                                setEditingScript({ name: '', content: '', description: '' });
                                setIsDialogOpen(true);
                            }}
                            size="sm"
                            className="bg-[var(--vf-primary)] hover:bg-[var(--vf-primary-hover)]"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            {t('dialer:callScripts.library.new')}
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    {scripts.length === 0 ? (
                        <p className="text-muted-foreground text-center py-4">
                            {t('dialer:callScripts.library.empty')}
                        </p>
                    ) : (
                        <div className="space-y-2">
                            {scripts.map((script) => (
                                <div
                                    key={script.id}
                                    className="bg-card rounded-lg p-3 border border-border cursor-pointer hover:bg-accent/50 transition-colors"
                                    onClick={() => {
                                        setEditingScript(script);
                                        setIsDialogOpen(true);
                                    }}
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <h4 className="font-medium text-foreground">{script.name}</h4>
                                            {script.description && (
                                                <p className="text-sm text-muted-foreground mt-1">{script.description}</p>
                                            )}
                                            <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{script.content}</p>
                                        </div>
                                        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => {
                                                    setEditingScript(script);
                                                    setIsDialogOpen(true);
                                                }}
                                            >
                                                <Edit className="w-4 h-4" />
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => deleteMutation.mutate(script.id)}
                                            >
                                                <Trash2 className="w-4 h-4 text-red-600" />
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* ── Edit / Create dialog ─────────────────────────────────────── */}
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{editingScript?.id ? t('dialer:callScripts.library.edit') : t('dialer:callScripts.library.new')} script</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <label className="text-sm font-medium mb-2 block">{t('common:field.name')}</label>
                            <Input
                                value={editingScript?.name || ''}
                                onChange={(e) => setEditingScript({ ...editingScript, name: e.target.value })}
                                placeholder={t('dialer:callScripts.namePlaceholder')}
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium mb-2 block">{t('dialer:callScripts.descriptionLabel')}</label>
                            <Input
                                value={editingScript?.description || ''}
                                onChange={(e) => setEditingScript({ ...editingScript, description: e.target.value })}
                                placeholder={t('dialer:callScripts.descriptionPlaceholder')}
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium mb-2 block">{t('dialer:scriptPanel.header.title')}</label>
                            <Textarea
                                value={editingScript?.content || ''}
                                onChange={(e) => setEditingScript({ ...editingScript, content: e.target.value })}
                                placeholder={t('dialer:callScripts.contentPlaceholder')}
                                className="min-h-[200px]"
                            />
                            {/* Not translated: the bracketed tokens must match the
                                placeholder above and the `content` interpolation,
                                same rule as ScriptPanel's variables hint. */}
                            <p className="text-xs text-muted-foreground mt-1">
                                {/* i18n-exempt: placeholder tokens the script engine substitutes (see above) */}
                                {t('dialer:callScripts.variablesLabel')} [prospect_name], [prospect_company], [caller_name], [company]
                            </p>
                        </div>
                        <Button
                            onClick={handleSave}
                            disabled={saveMutation.isPending}
                            className="w-full"
                        >
                            <Save className="w-4 h-4 mr-2" />
                            {t('common:actions.save')}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
